import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useUserStore } from '../store/userStore';
import { useUser, TeamRole } from '../hooks/useUser';
import { useMyLeagueStore } from '../store/myLeagueStore';

const ROLE_HIERARCHY = ['Owner', 'Co-Owner', 'GM', 'Manager', 'Head Coach', 'Coach'];

const getHighestRole = (roles?: TeamRole[]) => {
  if (!roles || roles.length === 0) return '';
  const sorted = [...roles].sort((a, b) => {
    const indexA = ROLE_HIERARCHY.indexOf(a.role_name);
    const indexB = ROLE_HIERARCHY.indexOf(b.role_name);
    const rankA = indexA === -1 ? 999 : indexA;
    const rankB = indexB === -1 ? 999 : indexB;
    return rankA - rankB;
  });
  return sorted[0].role_name;
};
import { Box, Container, Heading, VStack, HStack, Flex, Text, Icon, Button } from '@chakra-ui/react';
import { FiAlertTriangle } from 'react-icons/fi';
import JoinLeagues from '../components/JoinLeagues';
import StandingsChart from '../components/StandingsChart';
import NextGameLineupPanel from '../components/NextGameLineupPanel';
import ScheduleBanner from '../components/ScheduleBanner';
import type { BannerGameSelection } from '../components/ScheduleBanner';
import { UserProvider } from '../contexts/UserContext';
import apiClient from '../lib/api/apiClient';
import LeagueScoreboardCard from '../components/LeagueScoreboardCard';

const AuthenticatedLanding: React.FC = () => {
  const { user } = useUserStore();
  const { leagueAffiliations } = useUser();
  const myLeague = useMyLeagueStore((state) => state.myLeague);
  const embargoed = myLeague?.embargoed ?? 0;

  const [selectedBannerGame, setSelectedBannerGame] = useState<BannerGameSelection | null>(null);
  const handleBannerGameClick = useCallback((game: BannerGameSelection) => {
    setSelectedBannerGame(game);
  }, []);

  const displayName =
    user?.display_name
      ? user.display_name
      : user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`
        : user?.username
          ? user.username
          : user?.email
            ? user.email
            : 'User';

  const currentTeamId = user?.user_data?.myTeam;
  const currentAffiliation = leagueAffiliations?.find(
    (league) => Number(league.team_id) === Number(currentTeamId)
  );

  let welcomeMessage = `Welcome ${displayName}`;
  if (currentAffiliation) {
    const team = currentAffiliation.teams?.find((t) => Number(t.team_id) === Number(currentTeamId));
    const displayLeague = currentAffiliation.league_name.includes('League') ? currentAffiliation.league_name : currentAffiliation.league_name + ' League';
    const highestRole = getHighestRole(team?.roles);
    const parts = [highestRole, currentAffiliation.team_name, displayLeague].filter(Boolean);
    if (parts.length > 0) {
      welcomeMessage = `Welcome ${displayName}: ${parts.join(', ')}`;
    }
  }

  const [dashboardData, setDashboardData] = useState<any>(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Prevent double-fetching in React StrictMode
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    const fetchDashboardData = async () => {
      try {
        const res = await apiClient.get('/user/dashboard-data');
        setDashboardData(res.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        // setIsDashboardDataLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <UserProvider>
      <Box pt={14}>
        <ScheduleBanner onGameClick={handleBannerGameClick} />
        <Container maxW="98%" pt={8}>
          <VStack spacing={6} align="stretch">
            <Flex align="center" flexWrap="wrap" gap={3} rowGap={2}>
              <Heading
                as="h1"
                fontFamily="Audiowide"
                fontWeight="bold"
                color="white"
                textAlign="left"
                fontSize="2xl"
                m={0}
                p={0}
              >
                {welcomeMessage}
              </Heading>
              {currentAffiliation && (
                <Button
                  as={RouterLink}
                  to="/roster"
                  size="sm"
                  colorScheme="green"
                  flexShrink={0}
                >
                  Manage Team
                </Button>
              )}
            </Flex>

            {/* My Leagues Panel and Warning Panel - 50-50 on desktop, stacked on mobile */}
            {leagueAffiliations.length > 0 && (
              <HStack

                spacing={6}
                width="100%"
                flexDirection={{ base: "column", xl: "row" }}   // 👈 mobile = column, desktop = row
                align="stretch"

              >
                {(embargoed === 0 || embargoed === 2 || selectedBannerGame) ? (
                  <Box w={{ base: "100%", xl: "65%" }} flexShrink={0}>
                    <NextGameLineupPanel overrideGame={selectedBannerGame} />
                  </Box>
                ) : (
                  <Box w={{ base: "100%", xl: "65%" }} flexShrink={0}>
                    <LeagueScoreboardCard />
                  </Box>
                )}
                <VStack w={{ base: "100%", xl: "35%" }} align="stretch" spacing={6}>
                  <StandingsChart fullPage={false} />
                  {dashboardData?.notification?.subject && dashboardData?.notification?.html && (
                    <Box
                      bg="gray.700"
                      borderRadius="md"
                      p={4}
                      color="white"
                    >
                      <Flex align="center" mb={3} width="100%">
                        <Icon as={FiAlertTriangle} color="yellow.400" w={5} h={5} />
                        <Text fontWeight="bold" fontSize="lg" ml={2} color="white">
                          {dashboardData.notification.subject}
                        </Text>
                      </Flex>
                      <Box
                        width="100%"
                        color="white"
                        textAlign="left"
                        dangerouslySetInnerHTML={{ __html: dashboardData.notification.html }}
                        sx={{
                          '& td': {
                            padding: '8px',
                            color: 'white',
                            textAlign: 'left',
                          },
                          '& a': {
                            color: 'blue.300',
                            textDecoration: 'underline',
                          },
                          '& p': {
                            color: 'white',
                            textAlign: 'left',
                          },
                          '& *': {
                            color: 'white',
                            textAlign: 'left',
                          },
                        }}
                      />
                    </Box>
                  )}
                </VStack>
              </HStack>
            )}

            {/* Join Leagues Component
            <JoinLeagues /> */}
          </VStack>
        </Container>
      </Box>
    </UserProvider>
  );
};

export default AuthenticatedLanding; 